import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, fmtDate, scopeQuery, useScope } from "../api";
import { useT } from "../i18n";
import { useMe } from "./AdminShell";
import { ConfirmDelete, Field, Msg, Toggle, useFlash } from "../ui";
import { SendDialog } from "./Notifications";

/**
 * People & Roles (FR-ADM-04..06): one wide table, AND-filter chips, inline role select with
 * four-eyes for leader / pastor / admin, editor card below the table. Ages are computed here, never stored.
 */

export interface Person {
  userId: string;
  email: string;
  fullName: string | null;
  alias: string;
  phone: string | null;
  sex: string | null;
  birthday: string | null;
  address: string | null;
  postcode: string | null;
  maritalStatus: string | null;
  childrenBirthdays: string[];
  baptized: boolean | null;
  inTeam: boolean | null;
  fam: string | null;
  famStatus: string;
  role: string;
  active: boolean;
  joinedAt: string | null;
  points: number;
  deletionRequestedAt: string | null;
}
interface Approval {
  id: string;
  userId: string;
  name: string;
  role: string;
  proposedByName: string;
  note: string | null;
  createdAt: string;
  canDecide: boolean;
}
interface Data {
  people: Person[];
  total: number;
  approvals: Approval[];
  roles: string[];
  me: string;
}

/** Whole years between a date and now; months when under one year. */
export function ageOf(iso: string | null | undefined, now = new Date()): { years: number; months: number } | null {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  let years = now.getFullYear() - d.getFullYear();
  let months = now.getMonth() - d.getMonth();
  if (now.getDate() < d.getDate()) months--;
  if (months < 0) {
    years--;
    months += 12;
  }
  return { years, months: years * 12 + months };
}

type Filters = { fam: boolean; team: boolean; baptized: boolean; children: boolean };

export function AdminPeople() {
  const { t, locale } = useT();
  const me = useMe();
  const scope = useScope();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");
  const [filters, setFilters] = useState<Filters>({ fam: false, team: false, baptized: false, children: false });
  const [editing, setEditing] = useState<Person | "new" | null>(null);
  const [messageTo, setMessageTo] = useState<Person | null>(null);
  const [flash, run] = useFlash();
  const sq = scopeQuery(scope, "church", { q: search || undefined, role: role || undefined });
  const q = useQuery({ queryKey: ["admin", "people", scope.churchId, search, role], queryFn: () => api<Data>(`/admin/people${sq}`), enabled: !(scope.canPickScope && !scope.churchId) });
  const invalidate = () => Promise.all([qc.invalidateQueries({ queryKey: ["admin", "people"] }), qc.invalidateQueries({ queryKey: ["admin", "attention"] }), qc.invalidateQueries({ queryKey: ["admin", "today"] })]);
  const isAdmin = Boolean(me.data?.isSuperadmin || me.data?.membership?.role === "admin");

  const setRoleOf = useMutation({
    mutationFn: (p: { userId: string; role: string }) => api<{ pending: boolean }>(`/admin/people/${p.userId}/role${scopeQuery(scope, "church")}`, { method: "POST", json: { role: p.role } }),
    onSuccess: (r) => void invalidate().then(() => run(async () => undefined, r.pending ? t("admin.people.proposed") : t("admin.people.roleChanged"))),
  });
  const decide = useMutation({ mutationFn: (p: { id: string; decision: "approve" | "reject" }) => api(`/admin/people/approvals/${p.id}/${p.decision}${scopeQuery(scope, "church")}`, { method: "POST" }), onSuccess: invalidate });

  const people = useMemo(() => {
    const list = q.data?.people ?? [];
    return list.filter((p) => (!filters.fam || p.famStatus === "member") && (!filters.team || p.inTeam === true) && (!filters.baptized || p.baptized === true) && (!filters.children || p.childrenBirthdays.length > 0));
  }, [q.data, filters]);

  if (scope.canPickScope && !scope.churchId)
    return (
      <>
        <h1>{t("admin.people.title")}</h1>
        <p className="muted">{t("admin.fams.noChurch")}</p>
      </>
    );

  const yesNo = (v: boolean | null) => (v === null ? <span className="muted">{t("admin.people.unknown")}</span> : <span className={`pill ${v ? "ok" : ""}`}>{v ? t("admin.people.yes") : t("admin.people.no")}</span>);
  const ageLabel = (iso: string | null) => {
    const a = ageOf(iso);
    if (!a) return "";
    return a.years >= 1 ? t("admin.people.years", { n: a.years }) : t("admin.people.months", { n: a.months });
  };

  return (
    <>
      <p className="eyebrow">{t("admin.people.kicker", { n: people.length, m: q.data?.total ?? 0 })}</p>
      <h1>{t("admin.people.title")}</h1>
      <p className="muted">{t("admin.people.fullNamePrivacy")}</p>
      {!isAdmin ? <p className="muted">{t("admin.people.adminsOnly")}</p> : null}
      <Msg msg={flash} />

      {q.data && q.data.approvals.length > 0 ? (
        <div className="panel warn">
          <h2>{t("admin.people.approvals")}</h2>
          <div className="rows">
            {q.data.approvals.map((a) => (
              <div key={a.id} className="row fam-req">
                <div className="grow">
                  <strong>{t("admin.people.approvalRow", { name: a.name, role: t(`admin.people.role.${a.role}`), by: a.proposedByName })}</strong>
                  {a.note ? <div className="muted small">{a.note}</div> : null}
                  {!a.canDecide ? <div className="muted small">{t("admin.people.ownProposal")}</div> : null}
                </div>
                {isAdmin && a.canDecide ? (
                  <>
                    <button className="btn small" onClick={() => decide.mutate({ id: a.id, decision: "approve" })}>
                      {t("admin.people.approve")}
                    </button>
                    <button className="btn small ghost" onClick={() => decide.mutate({ id: a.id, decision: "reject" })}>
                      {t("admin.people.reject")}
                    </button>
                  </>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="toolbar">
        <input placeholder={t("admin.people.search")} value={search} onChange={(e) => setSearch(e.target.value)} style={{ minWidth: 320 }} />
        <select value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="">{t("admin.people.allRoles")}</option>
          {(q.data?.roles ?? ["member", "leader", "pastor", "admin"]).map((r) => (
            <option key={r} value={r}>
              {t(`admin.people.role.${r}`)}
            </option>
          ))}
        </select>
        <div className="chips">
          {(["fam", "team", "baptized", "children"] as const).map((k) => (
            <button key={k} type="button" className={`chip ${filters[k] ? "on" : ""}`} onClick={() => setFilters({ ...filters, [k]: !filters[k] })}>
              {t(`admin.people.filter${k[0]!.toUpperCase()}${k.slice(1)}`)}
            </button>
          ))}
        </div>
        {isAdmin ? (
          <button className="btn" onClick={() => setEditing("new")}>
            {t("admin.people.add")}
          </button>
        ) : null}
      </div>

      <div className="table-wrap">
        <table className="people">
          <thead>
            <tr>
              {["fullName", "alias", "email", "phone", "sex", "birthday", "address", "postcode", "fam", "team", "baptism", "marital", "children", "childrenAges", "points", "role"].map((k) => (
                <th key={k}>{t(`admin.people.col.${k}`)}</th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {people.length === 0 && !q.isLoading ? (
              <tr>
                <td colSpan={17} className="muted">
                  {t("admin.people.empty")}
                </td>
              </tr>
            ) : null}
            {people.map((p) => (
              <tr key={p.userId} className={p.deletionRequestedAt ? "dim" : ""}>
                <td>
                  <strong>{p.fullName ?? "—"}</strong>
                </td>
                <td>{p.alias}</td>
                <td>{p.email}</td>
                <td>{p.phone ? <a href={`tel:${p.phone}`}>{p.phone}</a> : "—"}</td>
                <td>{p.sex ?? "—"}</td>
                <td>{p.birthday ? `${fmtDate(p.birthday, locale)} · ${ageLabel(p.birthday)}` : "—"}</td>
                <td>{p.address ?? "—"}</td>
                <td>{p.postcode ?? "—"}</td>
                <td>{p.fam ?? yesNo(false)}</td>
                <td>{yesNo(p.inTeam)}</td>
                <td>{yesNo(p.baptized)}</td>
                <td>{p.maritalStatus ? t(`admin.people.marital.${p.maritalStatus}`) : "—"}</td>
                <td>{p.childrenBirthdays.length ? t("admin.people.childrenYes", { n: p.childrenBirthdays.length }) : yesNo(false)}</td>
                <td>{p.childrenBirthdays.map((b) => ageLabel(b)).join(", ") || "—"}</td>
                <td>{p.points}</td>
                <td>
                  {isAdmin && p.userId !== q.data?.me ? (
                    <select value={p.role} onChange={(e) => setRoleOf.mutate({ userId: p.userId, role: e.target.value })}>
                      {(q.data?.roles ?? []).map((r) => (
                        <option key={r} value={r}>
                          {t(`admin.people.role.${r}`)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="pill">{t(`admin.people.role.${p.role}`)}</span>
                  )}
                  {q.data?.approvals.some((a) => a.userId === p.userId) ? <div className="muted small">{t("admin.people.pending")}</div> : null}
                </td>
                <td className="nowrap">
                  <button className="btn small ghost" onClick={() => setEditing(p)}>
                    {t("admin.people.edit")}
                  </button>{" "}
                  <button className="btn small ghost" onClick={() => setMessageTo(p)}>
                    {t("admin.people.message")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing ? <PersonEditor key={editing === "new" ? "new" : editing.userId} person={editing === "new" ? null : editing} isAdmin={isAdmin} onClose={() => setEditing(null)} onSaved={() => void invalidate().then(() => setEditing(null))} /> : null}
      {messageTo ? <SendDialog recipient={{ userId: messageTo.userId, name: messageTo.fullName ?? messageTo.alias }} onClose={() => setMessageTo(null)} /> : null}
    </>
  );
}

type Form = {
  fullName: string;
  alias: string;
  email: string;
  phone: string;
  sex: string;
  birthday: string;
  address: string;
  postcode: string;
  maritalStatus: string;
  childrenBirthdays: string[];
  baptized: boolean | null;
  inTeam: boolean | null;
};

function PersonEditor({ person, isAdmin, onClose, onSaved }: { person: Person | null; isAdmin: boolean; onClose: () => void; onSaved: () => void }) {
  const { t } = useT();
  const scope = useScope();
  const [flash, run, busy] = useFlash();
  const [f, setF] = useState<Form>({
    fullName: person?.fullName ?? "",
    alias: person?.alias ?? "",
    email: person?.email ?? "",
    phone: person?.phone ?? "",
    sex: person?.sex ?? "",
    birthday: person?.birthday ?? "",
    address: person?.address ?? "",
    postcode: person?.postcode ?? "",
    maritalStatus: person?.maritalStatus ?? "",
    childrenBirthdays: person?.childrenBirthdays ?? [],
    baptized: person?.baptized ?? null,
    inTeam: person?.inTeam ?? null,
  });
  const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  const sq = scopeQuery(scope, "church");
  const age = ageOf(f.birthday || null);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!f.fullName.trim() || !f.email.trim()) {
      await run(async () => {
        throw new Error(t("admin.people.required"));
      });
      return;
    }
    const body = {
      fullName: f.fullName.trim(),
      alias: f.alias.trim() || undefined,
      email: f.email.trim(),
      phone: f.phone.trim() || null,
      sex: f.sex || null,
      birthday: f.birthday || null,
      address: f.address.trim() || null,
      postcode: f.postcode.trim() || null,
      maritalStatus: (f.maritalStatus || null) as "single" | "married" | "divorced" | null,
      childrenBirthdays: f.childrenBirthdays.filter(Boolean),
      baptized: f.baptized,
      inTeam: f.inTeam,
    };
    const ok = await run(() => (person ? api(`/admin/people/${person.userId}${sq}`, { method: "PATCH", json: body }) : api(`/admin/people${sq}`, { method: "POST", json: body })), person ? t("admin.people.saved") : t("admin.people.created"));
    if (ok) onSaved();
  };
  const remove = async () => {
    if (!person) return;
    if (await run(() => api(`/admin/people/${person.userId}${sq}`, { method: "DELETE" }), t("admin.people.deleted"))) onSaved();
  };

  return (
    <form className="form panel editor" onSubmit={save}>
      <h2>{person ? t("admin.people.edit") : t("admin.people.add")}</h2>
      <div className="two-col">
        <Field label={t("admin.people.col.fullName")}>
          <input required value={f.fullName} onChange={set("fullName")} />
        </Field>
        <Field label={t("admin.people.col.alias")}>
          <input value={f.alias} onChange={set("alias")} />
        </Field>
        <Field label={t("admin.people.col.email")}>
          <input type="email" required value={f.email} onChange={set("email")} disabled={Boolean(person) && !isAdmin} />
        </Field>
        <Field label={t("admin.people.col.phone")}>
          <input value={f.phone} onChange={set("phone")} />
        </Field>
        <Field label={t("admin.people.col.sex")}>
          <select value={f.sex} onChange={set("sex")}>
            <option value="">—</option>
            <option value="f">f</option>
            <option value="m">m</option>
            <option value="x">x</option>
          </select>
        </Field>
        <Field label={t("admin.people.col.birthday")} hint={age ? (age.years >= 1 ? t("admin.people.years", { n: age.years }) : t("admin.people.months", { n: age.months })) : undefined}>
          <input type="date" value={f.birthday} onChange={set("birthday")} />
        </Field>
        <Field label={t("admin.people.col.address")}>
          <input value={f.address} onChange={set("address")} />
        </Field>
        <Field label={t("admin.people.col.postcode")}>
          <input value={f.postcode} onChange={set("postcode")} />
        </Field>
        <Field label={t("admin.people.col.marital")}>
          <select value={f.maritalStatus} onChange={set("maritalStatus")}>
            <option value="">—</option>
            {["single", "married", "divorced"].map((m) => (
              <option key={m} value={m}>
                {t(`admin.people.marital.${m}`)}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="field">
        <span className="lbl">{t("admin.people.childrenBirthdays")}</span>
        <div className="chips">
          {f.childrenBirthdays.map((b, i) => {
            const a = ageOf(b || null);
            return (
              <span key={i} className="chip on" style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                <input type="date" value={b} onChange={(e) => setF({ ...f, childrenBirthdays: f.childrenBirthdays.map((x, j) => (j === i ? e.target.value : x)) })} />
                <span className="muted small">{a ? (a.years >= 1 ? t("admin.people.years", { n: a.years }) : t("admin.people.months", { n: a.months })) : ""}</span>
                <button type="button" className="btn small ghost" onClick={() => setF({ ...f, childrenBirthdays: f.childrenBirthdays.filter((_, j) => j !== i) })}>
                  ×
                </button>
              </span>
            );
          })}
          <button type="button" className="chip" onClick={() => setF({ ...f, childrenBirthdays: [...f.childrenBirthdays, ""] })}>
            + {t("admin.people.addChild")}
          </button>
        </div>
      </div>
      <div className="chips">
        <Toggle label={t("admin.people.col.baptism")} checked={f.baptized === true} onChange={(v) => setF({ ...f, baptized: v })} />
        <Toggle label={t("admin.people.col.team")} checked={f.inTeam === true} onChange={(v) => setF({ ...f, inTeam: v })} />
      </div>
      <div className="actions">
        <button className="btn" disabled={busy}>
          {t("admin.people.save")}
        </button>
        <button type="button" className="btn ghost" onClick={onClose}>
          {t("admin.people.cancel")}
        </button>
        <Msg msg={flash} />
        {person && isAdmin ? (
          <span style={{ marginInlineStart: "auto" }} title={t("admin.people.deleteHint")}>
            <ConfirmDelete label={t("admin.people.deletePerson")} onConfirm={() => void remove()} />
          </span>
        ) : null}
      </div>
      {person ? <p className="muted small">{t("admin.people.deleteHint")}</p> : null}
    </form>
  );
}
